import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfirmDialog, Meta, MetaRow, SegmentedControl } from '@ofself/shell';

// THE BUILDER — mission builder / editor, ported from legacy/app.js
// (2026-08-23, final phase). Lazy-create model: no DB write until the user
// actually types — the first keystroke (or first outliner add) mints the
// mission through window.ensureBuilderMission(), and typing is the commit.
// The .lane-* / .builder-* / .bf-* dress in legacy/style.css applies
// unchanged.
//
// THE INPUTS NEVER REMOUNT ACROSS THE MINT (the legacy promoteBuilderToReal
// law): title and description hold local state seeded when the builder
// OPENS, so the announce that follows minting can't clobber in-flight
// typing.

const CHIP_ROWS = [
  { field: 'status', values: ['open', 'in_progress', 'paused', 'completed', 'not_needed'] },
  { field: 'horizon', values: ['ongoing', 'short_term', 'long_term'] },
  { field: 'priority', values: ['low', 'medium', 'high'] },
];

export default function Builder({ active, mission }) {
  // Seed the form ONCE per builder entry (missions:builder-open fires from
  // openBuilder); later announces refresh only the outliner's tree.
  const [seed, setSeed] = useState(0);
  const [hasMission, setHasMission] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [fields, setFields] = useState({ status: 'in_progress', horizon: 'ongoing', priority: 'medium' });
  const [target, setTarget] = useState('');
  // Declared HERE with the other hooks, above the `active` return — see the
  // note on the useMemo below.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const titleDebounce = useRef(null);
  const descDebounce = useRef(null);
  // The statement block grows with its text rather than scrolling inside a
  // fixed box — same as the mission page's StatementBlock.
  const descRef = useRef(null);
  const grow = (el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };

  useEffect(() => {
    const onOpen = (e) => {
      const m = e.detail;
      setHasMission(!!m);
      setTitle(m && m.name !== 'Untitled mission' ? (m.name || '') : '');
      setDesc(m ? (m.statement || '') : '');
      setFields({
        status: (m && window.canonStatus?.(m.status)) || 'in_progress',
        horizon: (m && m.horizon) || 'ongoing',
        priority: (m && (m.priority || m.intention)) || 'medium',
      });
      setTarget((m && m.target_date) || '');
      setSeed((n) => n + 1);
    };
    document.addEventListener('missions:builder-open', onOpen);
    return () => document.removeEventListener('missions:builder-open', onOpen);
  }, []);
  // minting flips the mode without touching the form
  useEffect(() => {
    if (mission && !mission.error && active) setHasMission(true);
  }, [mission, active]);
  // Seeding a builder entry fills the statement from the mission; size the box
  // to whatever arrived, or it opens one line tall over several lines of text.
  useEffect(() => { grow(descRef.current); }, [seed, active]);

  const outlinerMission = mission && !mission.error ? mission : null;

  // GOALS AND ACTIONS ARE TWO LISTINGS, NOT A TREE (2026-09-20). The mission
  // holds them side by side; actions no longer hang off goals. Collected
  // depth-first and bucketed by kind so anything still nested from the tree era
  // is LISTED rather than hidden — the old shape reads, the new shape is what
  // gets written. A habit belongs with the actions: it is a recurring one.
  //
  // THIS STAYS ABOVE THE `active` RETURN. Every hook has to run on every render
  // or the count changes between them and React tears the tree down; putting
  // this useMemo below the early return is what blanked the page once already.
  const { goals, actions } = useMemo(() => {
    const goals = [], actions = [];
    const walk = (nodes) => {
      for (const n of (nodes || [])) {
        (n.kind === 'goal' ? goals : actions).push(n);
        walk(n.children);
      }
    };
    walk(outlinerMission?.items);
    return { goals, actions };
  }, [outlinerMission]);
  const count = goals.length + actions.length;

  if (!active) return null;

  const save = async (payload) => {
    await window.ensureBuilderMission();
    const id = window.builderMissionId?.();
    if (!id) return;
    await window.putJson(`/api/items/${id}`, payload);
    window.invalidateMissionCache?.();
    window.updateChatCtx?.();
  };

  const onTitle = (v) => {
    setTitle(v);
    window.updateChatCtx?.();
    clearTimeout(titleDebounce.current);
    titleDebounce.current = setTimeout(async () => {
      const val = v.trim();
      if (!val && !hasMission) return;
      try { await save({ title: val || 'Untitled mission' }); } catch (e) { console.warn(e); }
    }, 400);
  };
  const onDesc = (v) => {
    setDesc(v);
    clearTimeout(descDebounce.current);
    descDebounce.current = setTimeout(async () => {
      const val = v.trim();
      if (!val && !hasMission) return;
      try { await save({ value_json: { description: val } }); } catch (e) { console.warn(e); }
    }, 400);
  };

  return (
    // THE BUILDER IS FOR GETTING A MISSION DOWN (rebuilt 2026-09-20). Its job is
    // name it, say what it is for, then list the obvious goals and actions — so
    // the listings take the page, and the four properties sit between as a quiet
    // ledger: present and readable, but not holding the middle of the screen the
    // way four stacked rows of pills used to.
    <div className="builder-main" key={seed}>
      <div className="detail-head">
        <input className="detail-title" placeholder="name this mission"
               autoFocus value={title} onChange={(e) => onTitle(e.target.value)} />
        <textarea ref={descRef} rows={1} className="detail-statement"
                  placeholder="what this mission is for"
                  value={desc}
                  onChange={(e) => { onDesc(e.target.value); grow(e.target); }} />
      </div>

      {/* The ledger is the kit's <Meta> grammar with <SegmentedControl> for each
          one-of-many choice — real radio semantics and arrow keys, which the old
          .bf-chip buttons never had. Open: it sits between the name and the
          listings, quiet enough not to hold the page but there to be read. */}
      <div className="detail-ledger-block">
        <Meta className="detail-ledger">
          {CHIP_ROWS.map(({ field, values }) => (
            <MetaRow key={field} label={field}>
              <SegmentedControl
                options={values.map((v) => ({ value: v, label: v.replace('_', ' ') }))}
                value={fields[field]}
                onChange={async (v) => {
                  setFields((cur) => ({ ...cur, [field]: v }));
                  try { await save({ value_json: { [field]: v } }); } catch (e) { alert(`Couldn't save — ${e.message}`); }
                }}
              />
            </MetaRow>
          ))}
          <MetaRow label="target">
            {/* The kit dates with a native input too (Properties.jsx) — what it
                adds is the dress: no box, sitting in the ledger like a value. */}
            <input type="date" className="detail-date" value={target}
                   onChange={async (e) => {
                     setTarget(e.target.value);
                     try { await save({ value_json: { target_date: e.target.value || null } }); } catch (err) { alert(`Couldn't save — ${err.message}`); }
                   }} />
          </MetaRow>
        </Meta>
      </div>

      <div className="builder-lists">
        <ItemList label="Goals" kind="goal" items={goals}
                  lead="what are you working toward?" />
        <ItemList label="Actions" kind="action" items={actions}
                  lead="what will you actually do?" />
      </div>

      {hasMission && (
        <div className="builder-actions">
          {/* Was a hand-rolled .ghost-btn with its padding and font-size written
              inline, and a native confirm() for the are-you-sure. Both are the
              kit's: <Button> carries the quiet/danger voices, and ConfirmDialog
              is — in its own words — "the dialog every app rebuilds on top of
              Modal". A browser confirm() cannot be themed, cannot say what is
              about to be destroyed in the app's own voice, and looks like the
              operating system interrupting the page. */}
          <Button variant="quiet" tone="danger" onClick={() => setConfirmDelete(true)}>
            delete mission
          </Button>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          danger
          title="Delete this mission?"
          confirmLabel={deleting ? 'Deleting…' : 'Delete'}
          onCancel={() => { if (!deleting) setConfirmDelete(false); }}
          onConfirm={async () => {
            if (deleting) return;
            const id = window.builderMissionId?.();
            if (!id) { setConfirmDelete(false); return; }
            setDeleting(true);
            try {
              await window.cascadeDeleteMission(id);
              window.dropDeletedMission?.(id);
              window.builderClear?.();
              window.navToMissions?.();
              window.loadMissions?.();
            } catch (e) {
              setDeleting(false);
              setConfirmDelete(false);
              alert(`Couldn't delete — ${e.message}`);
            }
          }}
        >
          <p>
            <strong>{outlinerMission?.name || title || 'This mission'}</strong>
            {count > 0
              ? ` and the ${count} ${count === 1 ? 'item' : 'items'} inside it (${[
                  goals.length && `${goals.length} goal${goals.length === 1 ? '' : 's'}`,
                  actions.length && `${actions.length} action${actions.length === 1 ? '' : 's'}`,
                ].filter(Boolean).join(', ')}) will be deleted.`
              : ' will be deleted.'}
            {' '}This cannot be undone.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

const GLYPH = { goal: '◇', habit: '↻', action: '→' };
const ARTICLE = { goal: 'a goal', action: 'an action' };

// One listing — goals or actions, flat. No child affordance and no indent:
// the two kinds sit beside each other now, so there is nothing to nest under.
function ItemList({ label, kind, items, lead }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="builder-list">
      <h3 className="builder-list__head">
        {label}
        {items.length > 0 && <span className="builder-list__count">{items.length}</span>}
      </h3>
      {items.length > 0 ? (
        <ul className="builder-list__rows">
          {items.map((n) => (
            <li key={n.id}>
              <button type="button" className="builder-list__row" onClick={() => window.openSheet?.(n.id)}>
                <span className="builder-list__glyph" aria-hidden="true">{GLYPH[n.kind] || GLYPH[kind]}</span>
                <span className="builder-list__title">{n.title}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : !adding && <p className="builder-list__lead">{lead}</p>}
      {adding ? (
        <AddItemRow kind={kind} onClose={() => setAdding(false)} />
      ) : (
        <button type="button" className="builder-list__add" onClick={() => setAdding(true)}>
          <span className="builder-list__plus" aria-hidden="true">+</span> add {ARTICLE[kind]}
        </button>
      )}
    </section>
  );
}

// The draft line. It mints the mission on first commit (ensureBuilderMission),
// so there is no separate pre-mission path any more — the same row works before
// and after the mission exists. Enter KEEPS IT OPEN and clears it, because the
// job here is rattling off several in a row, not adding one and starting over.
function AddItemRow({ kind, onClose }) {
  const ref = useRef(null);
  const busy = useRef(false);
  useEffect(() => { ref.current?.focus(); }, []);
  const commit = async (keepOpen) => {
    if (busy.current) return;
    const t = (ref.current?.value || '').trim();
    if (!t) { onClose(); return; }
    busy.current = true;
    try {
      await window.ensureBuilderMission();
      const id = window.builderMissionId?.();
      await window.olCommitAdd(id, kind, t);
      await window.refreshMissionDetail?.();
      if (!keepOpen) { onClose(); return; }
      if (ref.current) { ref.current.value = ''; ref.current.focus(); }
      busy.current = false;
    } catch (e) {
      alert(`Create failed: ${e.message}`);
      busy.current = false;
    }
  };
  return (
    <div className="builder-list__row builder-list__row--draft">
      <span className="builder-list__glyph" aria-hidden="true">{GLYPH[kind]}</span>
      <input ref={ref} className="builder-list__draft" spellCheck={false}
             placeholder={`untitled ${kind}`}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { e.preventDefault(); commit(true); }
               if (e.key === 'Escape') { e.preventDefault(); onClose(); }
             }}
             onBlur={() => setTimeout(() => {
               if (busy.current) return;
               if ((ref.current?.value || '').trim()) commit(false); else onClose();
             }, 80)} />
    </div>
  );
}
